/* =====================================================================
 * useSidebarNavModel — 메인 사이드 패널에 실제로 노출되는 항목 목록.
 *
 * `Sidebar.tsx` 안에 인라인으로 있던 섹션 구성 로직을 그대로 떼어낸 것.
 * 분리한 이유는 사용자 가이드 페이지(`UserGuidePage`)의 목차가 사이드
 * 패널과 **같은 목록**이어야 하기 때문이다. 두 곳에 같은 조건문을 복붙해
 * 두면 mode 를 하나 추가할 때 한쪽만 고쳐도 타입 에러가 나지 않아 조용히
 * 어긋난다 (이 파일 곳곳의 "여기 빠뜨리면 안 뜬다" 주석이 그 사고 기록).
 *
 * 테넌트 플래그 / 권한 / 서버 허용목록 게이트를 모두 여기서 적용하므로,
 * 반환된 항목은 "지금 이 사용자에게 보이는 것"과 1:1 이다.
 * ===================================================================== */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useFeatureFlags } from '@auw/feature-flags';
import {
  ADMIN_SKILLS_MANAGE,
  ADMIN_USERS_VIEW,
  usePermission,
} from '@auw/permissions';
import {
  resolveSpecializedAgents,
  resolveTenantModeOrder,
  useTenant,
  type TenantSpecializedAgent,
} from '@auw/tenant';
import { useUserStore } from '@auw/stores';
import {
  WORKSPACE_MODES,
  WORKSPACE_ORDER,
  isWorkspaceMode,
  type WorkspaceMode,
} from '@auw/workspace-utils';

import { useAdminDashboardAccess } from '../../adminDashboard/useAdminDashboardAccess';

export interface SidebarNavItemConfig {
  mode: WorkspaceMode;
  label: string;
  unreadCount?: number;
}

/* ------------------------------------------------------------------ */
/* 구매 워크스페이스 — 조달/구매 업무 에이전트 묶음                        */
/*                                                                     */
/* 테넌트 `modeOrder` 안에서의 상대 순서는 그대로 두되, 렌더링만          */
/* "워크스페이스" 섹션에서 떼어내 별도 섹션으로 내린다. 테넌트가 해당      */
/* 모드를 끄면 목록이 비고, 섹션 자체가 렌더되지 않는다.                   */
/* ------------------------------------------------------------------ */
export const PURCHASE_WORKSPACE_MODES: readonly WorkspaceMode[] = [
  'gtc-agent',
  'purchase-agent',
  'official-doc-agent',
];

export interface SidebarNavModel {
  /** "워크스페이스" 섹션 — 구매 묶음을 제외한 테넌트 modeOrder. */
  workspaceItems: SidebarNavItemConfig[];
  /** "구매 워크스페이스" 섹션. */
  purchaseWorkspaceItems: SidebarNavItemConfig[];
  /** "전문 에이전트" 섹션 — mode 가 아니라 매니페스트 경로 기반. */
  specializedAgents: readonly TenantSpecializedAgent[];
  /** "팀 워크스페이스" 섹션. */
  teamWorkspaceItems: SidebarNavItemConfig[];
  /** "개인" 섹션 (위젯 / 피드 / 도구 / DM). */
  personalItems: SidebarNavItemConfig[];
  /** "간편 도구" 섹션. */
  utilityItems: SidebarNavItemConfig[];
  /** "마켓플레이스" 섹션 (에이전트 스킬). */
  marketplaceItems: SidebarNavItemConfig[];
  /** "관리" 섹션. */
  manageItems: SidebarNavItemConfig[];
  /** 팀 워크스페이스 섹션 및 개인/팀 토글을 그릴 수 있는지. */
  canManageTeamWorkspace: boolean;
  /** "관리" 섹션 자체의 노출 여부 (`sidebar.managementSection`). */
  managementSectionVisible: boolean;
  /** "관리자 콘솔" 진입점 노출 여부 (플래그 AND 권한). */
  adminConsoleVisible: boolean;
  /** Server-authorized Samsung admin access, independent of the generic role. */
  adminDashboardVisible: boolean;
}

export interface UseSidebarNavModelOptions {
  /**
   * DM 미읽음 합계. 사이드바만 `useDMUnreadPolling` 으로 폴링하므로
   * 뱃지 숫자는 호출부가 넘긴다 — 가이드 목차처럼 폴링이 필요 없는
   * 소비자가 이 훅을 쓰는 것만으로 DM 폴링을 켜지 않게 하기 위함이다.
   */
  readonly dmUnreadCount?: number;
}

export function useSidebarNavModel(
  options: UseSidebarNavModelOptions = {},
): SidebarNavModel {
  const { dmUnreadCount = 0 } = options;
  const { t } = useTranslation();
  const { isEnabled: isFeatureEnabled } = useFeatureFlags();
  const tenant = useTenant();
  const user = useUserStore((s) => s.user);

  const canManageUsers = usePermission(ADMIN_USERS_VIEW);
  const canManageSkills = usePermission(ADMIN_SKILLS_MANAGE);
  const {
    modeEnabled: adminDashboardModeEnabled,
    canView: canViewAdminDashboard,
  } = useAdminDashboardAccess();

  const sidebarTeamSwitcherEnabled = isFeatureEnabled('sidebar.teamSwitcher');
  const managementSectionVisible = isFeatureEnabled('sidebar.managementSection');
  const adminConsoleVisible =
    isFeatureEnabled('sidebar.adminConsoleLink') && canManageUsers;
  const canManageTeamWorkspace = sidebarTeamSwitcherEnabled && !!user;

  /**
   * Effective ordering for the "Workspace" section. Mirrors the
   * app-shell default (`WORKSPACE_ORDER`) unless the active tenant
   * manifest overrides it via `workspace.modeOrder` — B2C ships the
   * exact same list for zero regression (Phase 4c).
   */
  const effectiveWorkspaceOrder = useMemo(
    () => resolveTenantModeOrder(tenant, WORKSPACE_ORDER, isWorkspaceMode),
    [tenant],
  );

  /**
   * "전문 에이전트" 섹션에 노출할 항목. `enabled === false` 인 엔트리는
   * resolveSpecializedAgents 에서 이미 제외된 상태로 도착한다. 빈
   * 배열이면 섹션 전체가 노출되지 않는다 (테넌트가 specializedAgents
   * 필드를 비우면 자연스럽게 미노출).
   */
  const specializedAgents = useMemo(
    () => resolveSpecializedAgents(tenant),
    [tenant],
  );

  const enabledWorkspaceItems = useMemo<SidebarNavItemConfig[]>(
    () => effectiveWorkspaceOrder
      .filter((mode) => isFeatureEnabled(`mode.${mode}` as const))
      .map((mode) => ({
        mode,
        label: t(WORKSPACE_MODES[mode].labelKey),
      })),
    [effectiveWorkspaceOrder, isFeatureEnabled, t],
  );

  const workspaceItems = useMemo<SidebarNavItemConfig[]>(
    () => enabledWorkspaceItems.filter((item) => !PURCHASE_WORKSPACE_MODES.includes(item.mode)),
    [enabledWorkspaceItems],
  );

  const purchaseWorkspaceItems = useMemo<SidebarNavItemConfig[]>(
    () => enabledWorkspaceItems.filter((item) => PURCHASE_WORKSPACE_MODES.includes(item.mode)),
    [enabledWorkspaceItems],
  );

  // Tenant-level mode gates apply to every sidebar section, not just
  // the main workspace order. Without these checks a tenant that
  // disables `mode.message` / `mode.widget` / `mode.admin-page` /
  // ... in its manifest would still see those entries because the
  // section builders below render unconditionally. Centralising the
  // gate here keeps WorkspaceRouter's deep-link guard and the
  // sidebar in lockstep — the sidebar no longer hands the user a
  // link that lands on the placeholder page.
  const teamWorkspaceItems = useMemo<SidebarNavItemConfig[]>(
    () =>
      canManageTeamWorkspace && isFeatureEnabled('mode.admin-page')
        ? [{ mode: 'admin-page', label: t('nav.teamManage') }]
        : [],
    [canManageTeamWorkspace, isFeatureEnabled, t],
  );

  const personalItems = useMemo<SidebarNavItemConfig[]>(() => {
    const items: SidebarNavItemConfig[] = [];
    if (isFeatureEnabled('mode.widget')) {
      items.push({ mode: 'widget', label: t(WORKSPACE_MODES.widget.labelKey) });
    }
    if (isFeatureEnabled('mode.feed')) {
      items.push({ mode: 'feed', label: t(WORKSPACE_MODES.feed.labelKey) });
    }
    if (isFeatureEnabled('mode.tools')) {
      items.push({ mode: 'tools', label: t(WORKSPACE_MODES.tools.labelKey) });
    }
    if (isFeatureEnabled('mode.message')) {
      items.push({
        mode: 'message',
        label: t(WORKSPACE_MODES.message.labelKey),
        unreadCount: dmUnreadCount,
      });
    }
    return items;
  }, [dmUnreadCount, isFeatureEnabled, t]);

  // 대화 이력을 남기지 않는 단발성 도구들. 워크스페이스 모드와 달리
  // 항목을 눌러도 마지막 대화로 복귀하지 않고 항상 도구 페이지를 연다.
  const utilityItems = useMemo<SidebarNavItemConfig[]>(() => {
    const items: SidebarNavItemConfig[] = [];
    if (isFeatureEnabled('mode.translation')) {
      items.push({ mode: 'translation', label: t(WORKSPACE_MODES.translation.labelKey) });
    }
    return items;
  }, [isFeatureEnabled, t]);

  /* 마켓플레이스 — 사내에 공유된 에이전트 스킬을 찾아보고 내 스킬을 공유하는
   * 페이지. 간편 도구와 같이 대화 이력이 없는 단발성 페이지다. 개인 스코프라
   * 관리자 권한 게이트는 걸지 않고 테넌트 플래그만 본다. */
  const marketplaceItems = useMemo<SidebarNavItemConfig[]>(() => {
    const items: SidebarNavItemConfig[] = [];
    if (isFeatureEnabled('mode.skill-marketplace')) {
      items.push({
        mode: 'skill-marketplace',
        label: t(WORKSPACE_MODES['skill-marketplace'].labelKey),
      });
    }
    return items;
  }, [isFeatureEnabled, t]);

  const manageItems = useMemo<SidebarNavItemConfig[]>(() => {
    const items: SidebarNavItemConfig[] = [];
    if (isFeatureEnabled('mode.storage')) {
      items.push({ mode: 'storage', label: t(WORKSPACE_MODES.storage.labelKey) });
    }
    if (isFeatureEnabled('mode.plugin')) {
      items.push({ mode: 'plugin', label: t(WORKSPACE_MODES.plugin.labelKey) });
    }
    /* 에이전트 메모리 — 본인 대화에서 파악한 내용만 다루는 개인 스코프라
     * dashboard 와 같은 자세로 관리자 권한 게이트를 걸지 않는다. */
    if (isFeatureEnabled('mode.memory')) {
      items.push({ mode: 'memory', label: t(WORKSPACE_MODES.memory.labelKey) });
    }
    if (canManageUsers && isFeatureEnabled('mode.app-manage')) {
      items.push({ mode: 'app-manage' as const, label: t('nav.appManage') });
    }
    if (canManageSkills && isFeatureEnabled('mode.skill-create')) {
      items.push({ mode: 'skill-create' as const, label: t('nav.skillCreate') });
    }
    /* 대시보드 두 개는 이 섹션 맨 아래에 붙여 둔다. 나머지 항목은 무엇을
     * 관리하는 화면이고, 이 둘은 사용량을 읽는 화면이라 성격이 다르다.
     * 개인 → 어드민 순서라 범위가 넓어지는 방향으로 읽힌다. */
    if (isFeatureEnabled('mode.dashboard')) {
      items.push({ mode: 'dashboard', label: t(WORKSPACE_MODES.dashboard.labelKey) });
    }
    if (adminDashboardModeEnabled && canViewAdminDashboard) {
      items.push({
        mode: 'admin-dashboard' as const,
        label: t(WORKSPACE_MODES['admin-dashboard'].labelKey),
      });
    }
    return items;
  }, [
    t,
    canManageUsers,
    canManageSkills,
    isFeatureEnabled,
    adminDashboardModeEnabled,
    canViewAdminDashboard,
  ]);

  return {
    workspaceItems,
    purchaseWorkspaceItems,
    specializedAgents,
    teamWorkspaceItems,
    personalItems,
    utilityItems,
    marketplaceItems,
    manageItems,
    canManageTeamWorkspace,
    managementSectionVisible,
    adminConsoleVisible,
    adminDashboardVisible: canViewAdminDashboard,
  };
}
